/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { useContext, useState, ChangeEvent, useCallback } from "react"
import { useCartUpdater, useCartState } from "../../context/CartContext"
import AuthContext from "../../context/AuthContext"
import { CartOrderInstance } from "./cartTypes"
import { createOrder } from "./cartApi"
import { useHistory, useRouteMatch } from "react-router-dom"
import { TextField, Snackbar } from "@material-ui/core"
import { Theme } from "../types"
import Button from "../UI/Button/Button"
import { ProgressState } from "../UI/Button/buttonTypes"
import { Alert } from "@material-ui/lab"
import CartOrderList from "./List"
import CartBuyerSelector from "./BuyerSelector"
import { SelectorListItem } from "../UI/List/Selector/selectorTypes"

function CartBody() {
    const { clearCart: clearOrder } = useCartUpdater()
    const order = useCartState()
    const auth = useContext(AuthContext)
    const history = useHistory()
    const { path } = useRouteMatch()
    const [note, setNote] = useState<string>("")
    const [saveProgress, setSaveProgress] = useState<ProgressState>("ideal")
    const [selectedBuyer, setSelectedBuyer] = useState<SelectorListItem | null>(
        null
    )

    const handleSendOrderClick = () => {
        setSaveProgress("loading")
        const transformedOrder: CartOrderInstance[] = Object.keys(
            order.cart
        ).map((productId) => ({
            productId,
            calculatedPrice: order.cart[productId].calculatedPrice,
            orderCount: order.cart[productId].numberInOrder,
            perPiecePrice: order.cart[productId].perPiecePrice,
            currency: order.cart[productId].productInfo?.currency ?? "HUF",
        }))
        createOrder(
            transformedOrder,
            auth?.getAccessToken(),
            selectedBuyer?.id,
            note
        )
            .then((response) => {
                clearOrder()
                history.push(`${path}/${response.id}`, {
                    order,
                    selectedBuyer: selectedBuyer,
                })
            })
            .catch(() => {
                setSaveProgress("error")
            })
    }

    const handleNoteChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNote(event.target.value)
    }

    const handleErrorClose = () => {
        setSaveProgress("ideal")
    }

    const handleSelectBuyer = useCallback(
        (newValue: SelectorListItem | null) => {
            setSelectedBuyer(newValue)
        },
        []
    )

    return (
        <div css={styles.root}>
            {auth?.role === "admin" && (
                <CartBuyerSelector onChange={handleSelectBuyer} />
            )}
            <CartOrderList order={order} />
            <TextField
                multiline
                variant="outlined"
                label="Megjegyzés"
                color="primary"
                value={note}
                rows={4}
                onChange={handleNoteChange}
                css={styles.notes}
            />
            <Button
                onClick={handleSendOrderClick}
                progress={saveProgress}
                css={styles.orderBtn}
                testId="button_place_order"
            >
                Rendelés
            </Button>
            <Snackbar
                open={saveProgress === "error"}
                autoHideDuration={6000}
                onClose={handleErrorClose}
            >
                <Alert
                    severity="error"
                    elevation={6}
                    variant="filled"
                    onClose={handleErrorClose}
                >
                    Sikertelen művelet
                </Alert>
            </Snackbar>
        </div>
    )
}

export default CartBody

const styles = {
    root: css`
        display: flex;
        flex-direction: column;
    `,
    notes: (theme: Theme) => css`
        margin: ${theme.spaces.smallScale.s} 0;
    `,
    orderBtn: css`
        float: right;
    `,
}
